import { useCallback, useEffect, useState } from "react";
import useSearch from "../../utils/react-hook-search";

import Pagination from "../SharedComponents/Pagination";
import BouncingBalls from "./Loading/BouncingBalls";

export default function PaginatedSearch(props) {
    const {
        loading,
        items,
        itemName,
        ItemRow,
        searchAttributes,
        columnNames,
        customItemsPerPage,
        noTableHead,
        cards,
        filters,
        itemRowToggleOnChange,
        noPreviousAccessUser,
        accessDefaultToggleState,
        latestItemInteracted,
        updateItemStatus,
        setAlert,
        setAlertType,
        fetchItems,
        overflowVisible,
        customPlaceholder,
        customCardGrid,
        functionArray,
        initialSearchValue,
    } = props;

    const [pageCount, setPageCount] = useState(0);
    const [itemOffset, setItemOffset] = useState(0);
    const [currentPaginatedItems, setCurrentPaginatedItems] = useState([]);

    const itemsPerPage = customItemsPerPage ? customItemsPerPage : 8;

    const [filteredItems, search, setSearch] = useSearch(items, searchAttributes);

    const handlePageClick = (event) => {
        const newOffset = (event.selected * itemsPerPage) % items.length;
        setItemOffset(newOffset);
    };

    const runPagination = useCallback(
        (items) => {
            const endOffset = itemOffset + itemsPerPage;
            setCurrentPaginatedItems(items.slice(itemOffset, endOffset));
            setPageCount(Math.ceil(items.length / itemsPerPage));
        },
        [itemOffset, itemsPerPage]
    );

    useEffect(() => {
        runPagination(items);
    }, [items, runPagination]);

    useEffect(() => {
        runPagination(filteredItems);
    }, [filteredItems, itemOffset, itemsPerPage, runPagination]);

    useEffect(() => {
        if (initialSearchValue) {
            setSearch(initialSearchValue);
        }
    }, [initialSearchValue, setSearch]);

    return (
        <div className="flex flex-col gap-4">
            <input
                value={search}
                onChange={setSearch}
                type="text"
                name="search"
                id="search"
                placeholder={customPlaceholder ? customPlaceholder : "Search " + itemName}
                className={
                    "bg-white p-3 h-10 border border-none focus:outline-none text-sm shadow-sm rounded-md " +
                    "focus:ring-inset focus:ring-2 focus:ring-primary-300 focus:border-transparent block w-full " +
                    "text-slate-500"
                }
                autoFocus
            />

            {filters && <div className={"flex gap-2"}>{filters}</div>}

            {cards ? (
                <div
                    className={customCardGrid ? customCardGrid : "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4"}
                >
                    {loading ? (
                        <div className={"col-span-full flex items-center justify-center pt-20 pb-24 w-full"}>
                            <BouncingBalls />
                        </div>
                    ) : currentPaginatedItems.length === 0 ? (
                        <div className={"px-2 py-3 ml-2 text-slate-500 font-mono text-sm col-span-3"}>
                            Sorry there were no results for your search
                        </div>
                    ) : (
                        currentPaginatedItems.map((item, index) => (
                            <ItemRow
                                key={index}
                                item={item}
                                setAlert={setAlert}
                                setAlertType={setAlertType}
                                updateItemStatus={updateItemStatus}
                            />
                        ))
                    )}
                </div>
            ) : (
                <div
                    className={
                        "border-b border-none shadow-sm rounded-md " +
                        (overflowVisible ? "overflow-visible" : "overflow-hidden")
                    }
                >
                    <table className={"min-w-full"}>
                        {!noTableHead && (
                            <thead className={"bg-slate-50"}>
                                <tr className={"font-mono"}>
                                    {columnNames.map((columnName, index) => (
                                        <th
                                            key={index}
                                            scope={"col"}
                                            className={
                                                "px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase " +
                                                "font-mono tracking-wider overflow-hidden " +
                                                (index === 0
                                                    ? "rounded-tl-md"
                                                    : index === columnNames.length - 1
                                                    ? "rounded-tr-md"
                                                    : "hidden lg:table-cell")
                                            }
                                        >
                                            {columnName}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                        )}
                        <tbody className="bg-white  overflow-hidden">
                            {loading ? (
                                <tr>
                                    <td colSpan={columnNames.length}>
                                        <div className={"flex items-center justify-center pt-20 pb-24"}>
                                            <BouncingBalls />
                                        </div>
                                    </td>
                                </tr>
                            ) : currentPaginatedItems.length === 0 ? (
                                <tr>
                                    <td colSpan={columnNames.length}>
                                        <div className={"px-2 py-3 ml-2 text-slate-500 font-mono text-sm"}>
                                            Sorry there were no results for your search
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentPaginatedItems.map((item, index) => (
                                    <ItemRow
                                        key={index}
                                        item={item}
                                        itemRowToggleOnChange={itemRowToggleOnChange}
                                        noPreviousAccessUser={noPreviousAccessUser}
                                        accessDefaultToggleState={accessDefaultToggleState}
                                        latestItemInteracted={latestItemInteracted}
                                        updateItemStatus={updateItemStatus}
                                        setAlert={setAlert}
                                        setAlertType={setAlertType}
                                        fetchItems={fetchItems}
                                        lastItem={currentPaginatedItems.length - 1 === index}
                                        functionArray={functionArray}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            {items.length !== 0 && (
                <div className={"flex items-center justify-center w-full"}>
                    <Pagination handlePageClick={handlePageClick} pageCount={pageCount} />
                </div>
            )}
        </div>
    );
}
